<script setup lang="ts">
/**
 * SectionCard — one card whose header IS the section title.
 *
 * PO request 2026-08-18: standalone section headings (ยอดขายจากโปรโมชันและ
 * รายการเสริม / ผลการดำเนินงานรายภูมิภาค) floated between cards and read as
 * unfinished. A section is now a single card: title header + divider + body,
 * with the child components flattened into embedded sub-sections (their own
 * card chrome is stripped by the parent via :deep overrides).
 */
defineProps<{
  title: string;
  /** Accessible name when it differs from the visible title. */
  ariaLabel?: string;
  /** Remove card chrome when the section is rendered inside a parent card. */
  embedded?: boolean;
}>();
</script>

<template>
  <component :is="embedded ? 'div' : 'section'" :class="embedded ? 'scard--embedded' : 'scard'" :aria-label="ariaLabel ?? title">
    <h2 v-if="!embedded" class="card-head">{{ title }}</h2>
    <div class="scard__body">
      <slot />
    </div>
  </component>
</template>

<style scoped>
/* Same card treatment as the page's KPI cards (design system: surface,
 * 1px line, card radius, no shadow) — sections differ only by having no
 * status accent and an internal header + divider. */
.scard {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  padding: 0.85rem;
  margin-bottom: 1rem;
  box-shadow: none;
}

/* Title: global .card-head (canonical card title, 2026-08-18). The body
 * spacing compensates for the header's own divider + margin. */
.scard__body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.scard--embedded {
  display: block;
  margin: 0;
}
</style>
